package com.arena_web.ui.components.business

import androidx.compose.runtime.Composable
import com.arena_web.navigation.Routes
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.icons.TintedGlyph
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

/**
 * Empty state shown on all business pages (except Settings) when no club is selected.
 * "Go to Club Setup" navigates to /settings where the create-club form lives.
 */
@Composable
fun NoClubsEmptyState() {
    val ctx = rememberPageContext()

    // Centered wrapper with top padding
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sPaddingTop(80),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Outer card — backdrop blur wrapper (w-1028)
        Box(
            modifier = Modifier
                .sWidth(1028)
                .sBorderRadius(28)
                .styleModifier {
                    property(
                        "background",
                        "linear-gradient(178deg, rgba(60, 121, 241, 0.135) 14%, rgba(60, 121, 241, 0.045) 69%)"
                    )
                    property("backdrop-filter", "blur(${AppEffects.BACKGROUND_BLUR_MEDIUM}px)")
                    property("max-width", "100%")
                }
        ) {
            // Inner card (h-416, fixed)
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(416)
                    .sBorderRadius(28)
                    .sPadding(22, 24)
                    .sPaddingBottom(34)
                    .styleModifier {
                        property(
                            "background",
                            "linear-gradient(10deg, rgba(0, 59, 174, 0.03) 27%, rgba(3, 87, 230, 0.03) 68%)"
                        )
                        property("border", "1px solid rgba(41, 52, 138, 0.2)")
                    },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Content (h-372, gap-32)
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(372)
                        .sGap(32),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    // Dashboard icon
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sPadding(5),
                        contentAlignment = Alignment.Center
                    ) {
                        TintedGlyph(src = AppIcons.Dashboard, sizePx = 125)
                    }

                    // Text + button (flex-grow, gap-40)
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .flexGrow(1)
                            .sGap(40),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        // Text block (gap-12)
                        Column(
                            modifier = Modifier.fillMaxWidth().sGap(12),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            SpanText(
                                "No clubs added yet",
                                modifier = Modifier
                                    .sFontSize(34)
                                    .fontWeight(FontWeight.SemiBold)
                                    .sLineHeight(40)
                                    .sLetterSpacing(0.34)
                                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                                    .textAlign(TextAlign.Center)
                            )
                            SpanText(
                                "Create a club profile to start managing your schedules, pricing models, and additional sports services in one place.",
                                modifier = Modifier
                                    .sFontSize(20)
                                    .fontWeight(FontWeight.Medium)
                                    .sLineHeight(26)
                                    .sLetterSpacing(0.2)
                                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                                    .textAlign(TextAlign.Center)
                            )
                        }

                        // "Go to Club Setup" button
                        Box(
                            modifier = Modifier
                                .sHeight(44)
                                .sBorderRadius(22)
                                .sPaddingLeft(18)
                                .sPaddingRight(4)
                                .sPaddingTop(4)
                                .sPaddingBottom(4)
                                .backgroundColor(AppColors.Primary)
                                .cursor(Cursor.Pointer)
                                .onClick { ctx.router.navigateTo(Routes.SETTINGS) }
                                .styleModifier {
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("gap", s(12))
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                "Go to Club Setup",
                                modifier = Modifier
                                    .bodyLMedium()
                                    .color(AppColors.PrimaryWhite)
                                    .styleModifier { property("white-space", "nowrap") }
                            )
                            Box(
                                modifier = Modifier
                                    .sSize(36)
                                    .sBorderRadius(100)
                                    .backgroundColor(SportColors.Football.secondary)
                                    .styleModifier {
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("justify-content", "center")
                                    },
                                contentAlignment = Alignment.Center
                            ) {
                                Icon(
                                    src = AppIcons.ArrowUpOutline,
                                    modifier = Modifier.sSize(18).styleModifier {
                                        property("transform", "rotate(45deg)")
                                        property("filter", "brightness(0) invert(1)")
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}
